/**
 * Upload Progress Component
 *
 * Progress indicator for file uploads with animated bar, status icons,
 * and cancel/dismiss actions.
 */

import { View, Text } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
  FadeIn,
  FadeOut,
  withRepeat,
  withSequence,
  useSharedValue,
} from 'react-native-reanimated';
import { FileText, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import type { UploadProgress as UploadProgressType } from '@/lib/storage';
import { useEffect } from 'react';
import AnimatedPressable from '@/components/AnimatedPressable';

interface UploadProgressProps {
  fileName: string;
  progress: UploadProgressType | null;
  status: 'idle' | 'uploading' | 'success' | 'error';
  error?: string;
  onCancel?: () => void;
  onDismiss?: () => void;
}

export function UploadProgress({
  fileName,
  progress,
  status,
  error,
  onCancel,
  onDismiss,
}: UploadProgressProps) {
  const colors = useThemeColors();
  const rotation = useSharedValue(0);

  // Animate loader rotation when uploading
  useEffect(() => {
    if (status === 'uploading') {
      rotation.value = withRepeat(
        withSequence(
          withTiming(360, { duration: 1000 }),
        ),
        -1,
        false
      );
    } else {
      rotation.value = 0;
    }
  }, [status, rotation]);

  const progressBarStyle = useAnimatedStyle(() => {
    return {
      width: withTiming(`${progress?.percentage || 0}%`, { duration: 200 }),
    };
  });

  const loaderStyle = useAnimatedStyle(() => {
    return {
      transform: [{ rotate: `${rotation.value}deg` }],
    };
  });

  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return (
          <Animated.View style={loaderStyle}>
            <Loader2 size={20} color={colors.primary[600]} />
          </Animated.View>
        );
      case 'success':
        return <CheckCircle size={20} color={colors.success} />;
      case 'error':
        return <AlertCircle size={20} color={colors.error} />;
      default:
        return <FileText size={20} color={colors.text.secondary} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'uploading':
        return colors.primary[600];
      case 'success':
        return colors.success;
      case 'error':
        return colors.error;
      default:
        return colors.text.secondary;
    }
  };

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(200)}
      className="rounded-xl p-4"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      <View className="flex-row items-center gap-3">
        <View
          className="w-10 h-10 rounded-lg items-center justify-center"
          style={{ backgroundColor: `${getStatusColor()}15` }}
        >
          {getStatusIcon()}
        </View>

        <View className="flex-1">
          <Text
            style={{ color: colors.text.primary }}
            className="font-medium"
            numberOfLines={1}
          >
            {fileName}
          </Text>

          {status === 'uploading' && progress && (
            <View className="mt-2">
              <View
                className="h-1.5 rounded-full overflow-hidden"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Animated.View
                  className="h-full rounded-full"
                  style={[
                    { backgroundColor: colors.primary[600] },
                    progressBarStyle,
                  ]}
                />
              </View>
              <Text
                style={{ color: colors.text.tertiary }}
                className="text-xs mt-1"
              >
                {progress.percentage}% • {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
              </Text>
            </View>
          )}

          {status === 'success' && (
            <Text style={{ color: colors.success }} className="text-sm mt-1">
              Upload complete
            </Text>
          )}

          {status === 'error' && error && (
            <Text style={{ color: colors.error }} className="text-sm mt-1">
              {error}
            </Text>
          )}
        </View>

        {status === 'uploading' && onCancel && (
          <AnimatedPressable
            onPress={onCancel}
            className="p-2 rounded-lg"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <X size={16} color={colors.text.secondary} />
          </AnimatedPressable>
        )}

        {(status === 'success' || status === 'error') && onDismiss && (
          <AnimatedPressable
            onPress={onDismiss}
            className="p-2 rounded-lg"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <X size={16} color={colors.text.secondary} />
          </AnimatedPressable>
        )}
      </View>
    </Animated.View>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
